import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'

import { BRAND_OVERRIDE_PARAM } from '_constants/kb-links.constants'
import {
  buildHelpDocsUrl,
  extractArticleSlug,
  isValidBrandSlug,
  readBrandHint,
  writeBrandHint,
} from '_utils/brand-hint.util'
import { fetchTenantConfig } from '../../services/tenant.service'
import { brandPickerSchema, type BrandPickerValues } from './kb-redirect.schema'
import { styles } from './kb-redirect.styles'

const CenteredPanel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={styles.page}>
    <Stack spacing={2} alignItems="center" textAlign="center" sx={styles.panel}>
      {children}
    </Stack>
  </Box>
)

export default function KbRedirect() {
  const { t } = useTranslation('core')

  const articleSlug = useMemo(() => extractArticleSlug(), [])

  // A ?b= override is attacker-supplied, so it is validated against tenant-config before it
  // can overwrite a visitor's saved brand hint. The cookie is trusted: a successful login
  // on that brand wrote it.
  const [candidateBrand, setCandidateBrand] = useState<string | null>(() => {
    const override = new URLSearchParams(window.location.search).get(BRAND_OVERRIDE_PARAM)
    return isValidBrandSlug(override) ? override : null
  })

  const trustedBrand = useMemo(() => (candidateBrand ? null : readBrandHint()), [candidateBrand])

  const {
    isFetching: isCheckingBrand,
    isError: brandCheckFailed,
    isSuccess: brandCheckPassed,
  } = useQuery(
    ['tenantConfig', candidateBrand],
    () => fetchTenantConfig(candidateBrand as string),
    {
      enabled: !!candidateBrand,
      retry: false,
    }
  )

  const resolvedBrand = brandCheckPassed && candidateBrand ? candidateBrand : trustedBrand

  const { control, handleSubmit, formState } = useForm<BrandPickerValues>({
    resolver: zodResolver(brandPickerSchema),
    defaultValues: { brand: '' },
    mode: 'onSubmit',
  })

  useEffect(() => {
    if (!articleSlug || !resolvedBrand) {
      return
    }

    const target = buildHelpDocsUrl(resolvedBrand, articleSlug)
    if (!target) {
      return
    }

    writeBrandHint(resolvedBrand)
    window.location.replace(target)
  }, [articleSlug, resolvedBrand])

  if (!articleSlug) {
    return (
      <CenteredPanel>
        <Typography variant="h6">{t('helpDocs.redirect.title')}</Typography>
        <Typography color="text.secondary">{t('helpDocs.redirect.missingSlug')}</Typography>
      </CenteredPanel>
    )
  }

  if (resolvedBrand || isCheckingBrand) {
    return (
      <CenteredPanel>
        <CircularProgress size={28} />
        <Typography color="text.secondary">{t('helpDocs.redirect.resolving')}</Typography>
      </CenteredPanel>
    )
  }

  const errorMessage =
    formState.errors.brand || brandCheckFailed ? t('helpDocs.redirect.notFound') : null

  return (
    <CenteredPanel>
      <Typography variant="h6">{t('helpDocs.redirect.title')}</Typography>
      <Typography color="text.secondary">{t('helpDocs.redirect.prompt')}</Typography>

      <Box
        component="form"
        onSubmit={handleSubmit((values) => setCandidateBrand(values.brand.trim().toLowerCase()))}
        sx={styles.form}
      >
        <Stack spacing={2}>
          <Controller
            name="brand"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus
                fullWidth
                label={t('helpDocs.redirect.brandLabel')}
                helperText={errorMessage || t('helpDocs.redirect.brandHelper')}
                error={!!errorMessage}
                inputProps={{ 'data-testid': 'kb-redirect-brand-input' }}
              />
            )}
          />
          <Button type="submit" variant="contained" data-testid="kb-redirect-submit">
            {t('helpDocs.redirect.continue')}
          </Button>
        </Stack>
      </Box>
    </CenteredPanel>
  )
}
